import { motion } from "framer-motion";
import { Phone, Sparkles } from "lucide-react";
import { PHONE_TEL } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
import { TiltCard } from "@/components/spa/TiltCard";

export const Offers = () => {
  const { t } = useLang();
  return (
    <section id="offers" data-testid="offers-section" className="relative py-24 lg:py-40 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050706] via-[#0e0c07] to-[#050706]" />
      <span aria-hidden="true" className="ghost-char top-8 left-2">惠</span>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[#c5a059]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{t.offers.label}</span>
          </div>
          <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2]">
            {t.offers.title.pre}<span className="italic gold-shimmer-text">{t.offers.title.it}</span>{t.offers.title.post}
          </h2>
        </motion.div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.offers.cards.map((o, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <TiltCard
                testid={`offer-card-0${i + 1}`}
                max={5}
                lift={6}
                glow="0 22px 55px -18px rgba(197,160,89,0.5)"
                className="group h-full relative border border-[#c5a059]/25 bg-[#0c100e]/60 backdrop-blur-sm p-8 hover:border-[#c5a059]/60 overflow-hidden"
              >
                <span className="absolute -top-8 -right-4 font-serif-display text-[7rem] leading-none text-[#c5a059]/[0.06] select-none group-hover:text-[#c5a059]/10 transition-colors duration-500">
                  0{i + 1}
                </span>
                <Sparkles size={18} className="text-[#c5a059]" />
                <span className="mt-6 block text-[10px] tracking-[0.3em] uppercase text-[#8a9690]">{o.tag}</span>
                <h3 className="mt-3 font-serif-display text-2xl lg:text-[1.7rem] leading-tight text-[#f0f4f2] group-hover:text-[#dcc286] transition-colors duration-300">
                  {o.title}
                </h3>
                <p className="mt-4 text-sm text-[#8a9690] font-light leading-relaxed">{o.text}</p>
              </TiltCard>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-12 flex flex-wrap items-center gap-6"
        >
          <a
            href={PHONE_TEL}
            data-testid="offers-call-button"
            className="inline-flex items-center gap-3 bg-[#c5a059] text-[#050706] hover:bg-[#dcc286] px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300"
          >
            <Phone size={14} /> {t.offers.cta}
          </a>
          <span className="text-xs text-[#8a9690]/70 font-light">{t.offers.note}</span>
        </motion.div>
      </div>
    </section>
  );
};
